import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { errorText, useToast } from '../components/ui.js'
import { ApiError } from './api.js'
import { readAppLocked } from './app-lock-state.js'
import { CommandRejected, useBackend } from './backend.js'
import { type ActionCommand, commandFor, planNotificationTap } from './notification-actions.js'
import { answerOnce } from './private-inbox.js'
import { qk, useInbox } from './queries.js'
import { onServiceWorkerMessage } from './sw-register.js'
import type { InboxItem } from './types.js'

interface Wanted {
  id: string
  action: string
  url?: string
  /** R3: Viko's app lock at tap time (unknown counts as locked). */
  locked: boolean
}

/** Record a tap together with the lock state at that moment, so unlocking later never signs it. */
const withLock = (w: Omit<Wanted, 'locked'>, set: (w: Wanted) => void) =>
  void readAppLocked().then((locked) => set({ ...w, locked }))

const answerIds = new Map<string, string>()

/**
 * A notification button (Allow / Deny / an option) was tapped. The service worker cannot sign, so
 * it hands the tap to the page, which signs the command with this device's key and sends it once
 * (private asks are claimed on the hub first: a second device or a double tap cannot answer twice).
 */
export function useNotificationActions() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const toast = useToast()
  const inbox = useInbox()
  const [wanted, setWanted] = useState<Wanted | null>(null)

  useEffect(
    () =>
      onServiceWorkerMessage((m) => {
        if (m.type === 'notification-action' && m.id && m.action)
          withLock({ id: m.id, action: m.action, ...(m.url ? { url: m.url } : {}) }, setWanted)
      }),
    [],
  )
  // Cold start: the worker opened the app with the tap in the URL (`?na=<item>&act=<action>`).
  useEffect(() => {
    const u = new URL(location.href)
    const id = u.searchParams.get('na')
    const action = u.searchParams.get('act')
    if (!id || !action) return
    u.searchParams.delete('na')
    u.searchParams.delete('act')
    history.replaceState(history.state, '', `${u.pathname}${u.search}${u.hash}`)
    withLock({ id, action }, setWanted)
  }, [])

  const items = inbox.data
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per tap
  useEffect(() => {
    if (!wanted || !items) return
    setWanted(null)
    const item = items.find((i) => (i.itemId ?? i.id) === wanted.id || i.id === wanted.id)
    // R3: never sign from a notification while Viko is locked; open the ask behind the unlock screen.
    const plan = planNotificationTap({
      locked: wanted.locked,
      ask: item?.event,
      action: wanted.action,
    })
    if (plan === 'unlock-first') {
      toast('Unlock Viko to answer.')
      if (item?.runId) void nav({ to: '/s/$runId', params: { runId: item.runId } })
      return
    }
    if (plan === 'already-answered' || !item?.event) {
      toast('That request was already answered.')
      return
    }
    const cmd = commandFor(item.event, wanted.action)
    if (plan === 'open' || !cmd) {
      if (item.runId) void nav({ to: '/s/$runId', params: { runId: item.runId } })
      return
    }
    void resolve(item, cmd)
  }, [wanted, items])

  async function resolve(item: InboxItem, cmd: ActionCommand) {
    const machineId = item.machineId
    if (!machineId) {
      toast('This request has no machine yet. Open it to answer.', 'error')
      return
    }
    try {
      const send = (commandId?: string) =>
        b.dispatch(cmd.kind, machineId, cmd.payload as never, commandId ? { id: commandId } : {})
      let already = false
      if (item.private) {
        const r = await answerOnce(
          {
            claim: (id, commandId) => b.api.answerClaim(id, commandId),
            send: (commandId) => send(commandId),
            isDuplicate: (e) =>
              (e instanceof ApiError && e.code === 'id_conflict') ||
              (e instanceof CommandRejected && e.outcome.status === 'duplicate'),
          },
          answerIds,
          item.itemId ?? item.id,
        )()
        already = r === 'already-answered'
      } else await send()
      qc.setQueryData<InboxItem[]>(qk.inbox, (prev) => prev?.filter((i) => i.id !== item.id))
      toast(
        already
          ? 'Already answered on another device'
          : cmd.kind === 'run.approve'
            ? cmd.payload.decision === 'deny'
              ? 'Denied'
              : 'Approved'
            : 'Answer sent',
      )
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
}
